import { useCallback, useEffect, useMemo, useState } from 'react';
import { earlyAccumulationAPI } from '../../services/api';
import { buildPipeline, buildPromotions, getLocale } from './shared';

const fetchEarlyAccumulationDashboardData = async (filters) => {
  const params = {
    stage: 'FORMING,MATURE,BREAKOUT,NEAR_MISS',
    early_only: false,
    near_miss_only: false,
    forming_only: false,
    min_score: filters.min_score,
    strong_only: filters.strong_only,
    limit: filters.limit,
  };
  if (filters.sector) params.sector = filters.sector;

  const [candidatesRes, historyRes] = await Promise.all([
    earlyAccumulationAPI.getCandidates(params),
    earlyAccumulationAPI.getHistory({
      days: 14,
      min_score: filters.min_score,
      strong_only: filters.strong_only,
    }),
  ]);

  return {
    data: candidatesRes.data,
    history: historyRes?.data?.history || [],
  };
};

const triggerEarlyAccumulationCalculation = async () => {
  const response = await fetch('/api/early-accumulation/calculate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Calculation failed');
  }
};

export function useEarlyAccumulationDashboard({ t, language }) {
  const locale = getLocale(language);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filters, setFilters] = useState({
    min_score: 55,
    sector: '',
    strong_only: true,
    limit: 100,
  });
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setHistoryLoading(true);
    setError('');
    try {
      const dashboardData = await fetchEarlyAccumulationDashboardData(filters);
      setData(dashboardData.data);
      setHistory(dashboardData.history);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || requestError.message || t('dashboard.earlyAccumulation.errors.loadFailed'));
      setHistory([]);
    } finally {
      setLoading(false);
      setHistoryLoading(false);
    }
  }, [filters, t]);

  const handleCalculate = useCallback(async () => {
    setCalculating(true);
    setError('');
    try {
      await triggerEarlyAccumulationCalculation();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await load();
    } catch (requestError) {
      setError(requestError.message || 'Failed to calculate Early Accumulation scores');
    } finally {
      setCalculating(false);
    }
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  const candidates = useMemo(() => data?.candidates || [], [data]);
  const scanDateValue = data?.score_date || '-';
  const pipeline = useMemo(() => buildPipeline(candidates), [candidates]);
  const promotions = useMemo(() => buildPromotions(candidates), [candidates]);
  const marketRegime = candidates[0]?.market_regime || data?.market_regime;

  return {
    locale,
    loading,
    calculating,
    error,
    data,
    history,
    historyLoading,
    selectedRow,
    setSelectedRow,
    filters,
    setFilters,
    showAdvanced,
    setShowAdvanced,
    showHistory,
    setShowHistory,
    load,
    handleCalculate,
    candidates,
    scanDateValue,
    pipeline,
    promotions,
    marketRegime,
  };
}
